import {
  ActionIcon,
  Alert,
  Autocomplete,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconMasksTheater,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/page-header';
import { ApiError, ApiErrorCode, NetworkError } from '@/lib/api-error';
import {
  OFFERED,
  SUGGESTIONS,
  blankScene,
  createCharacter,
  deleteCharacter,
  deleteScene,
  listCharacters,
  saveScene,
  updateCharacter,
  type Character,
  type CharacterDraft,
  type Scene,
  type SceneDraft,
} from '@/lib/characters';
import { listLanguages, type Language } from '@/lib/languages';
import { listVoices, voicesFor, type SpeechVoice } from '@/lib/speech';
import classes from './settings-page.module.css';

type Subject = { mode: 'create' } | { mode: 'edit'; character: Character };

/** Which character's scene is being written, and in which language. */
interface SceneSubject {
  character: Character;
  scene: Scene | null;
  languageCode: string;
}

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/**
 * Client-side checks, mirroring what the server enforces.
 *
 * A convenience so a typo does not cost a round trip — not a control. This
 * bundle is public and modifiable, so the API validates the same fields again
 * and is the only thing that decides what gets stored.
 */
function validate(
  draft: CharacterDraft,
): Partial<Record<keyof CharacterDraft, string>> {
  const errors: Partial<Record<keyof CharacterDraft, string>> = {};

  if (!draft.name.trim()) errors.name = 'Required.';
  if (!draft.initial.trim()) errors.initial = 'Required.';
  if (!draft.blurb.trim()) errors.blurb = 'Required.';
  if (!draft.tolerance.trim()) errors.tolerance = 'Required.';
  if (!draft.nextUp.trim()) errors.nextUp = 'Required.';
  if (!draft.nextUpReason.trim()) errors.nextUpReason = 'Required.';

  return errors;
}

/** Every field of a scene has to be written; a half-authored one is worse. */
function sceneIsComplete(draft: SceneDraft): boolean {
  const scalars = [
    draft.setting,
    draft.wants,
    draft.register,
    draft.silencePrompt,
    draft.silencePromptGloss,
    draft.closing,
    draft.closingGloss,
  ];
  if (scalars.some((value) => !value.trim())) return false;

  return draft.turns.every(
    (turn) =>
      turn.line.trim() &&
      turn.gloss.trim() &&
      turn.sayItForMe.trim() &&
      turn.hint.trim() &&
      turn.suggestions.length === SUGGESTIONS &&
      turn.suggestions.every((reply) => reply.trim()),
  );
}

interface CharacterFormProps {
  subject: Subject;
  onClose: () => void;
  onSaved: (character: Character, mode: Subject['mode']) => void;
}

/**
 * One form for both adding and editing, mounted per subject with a `key` so an
 * abandoned edit cannot bleed into the next one.
 */
function CharacterForm({ subject, onClose, onSaved }: CharacterFormProps) {
  const [draft, setDraft] = useState<CharacterDraft>(() =>
    subject.mode === 'edit'
      ? {
          name: subject.character.name,
          initial: subject.character.initial,
          blurb: subject.character.blurb,
          tolerance: subject.character.tolerance,
          nextUp: subject.character.nextUp,
          nextUpReason: subject.character.nextUpReason,
          displayOrder: subject.character.displayOrder,
        }
      : {
          name: '',
          initial: '',
          blurb: '',
          tolerance: '',
          nextUp: '',
          nextUpReason: '',
          displayOrder: null,
        },
  );
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof CharacterDraft, string>>
  >({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    const errors = validate(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const trimmed: CharacterDraft = {
      name: draft.name.trim(),
      initial: draft.initial.trim(),
      blurb: draft.blurb.trim(),
      tolerance: draft.tolerance.trim(),
      nextUp: draft.nextUp.trim(),
      nextUpReason: draft.nextUpReason.trim(),
      displayOrder: draft.displayOrder,
    };

    setIsSaving(true);
    setSubmitError(null);
    try {
      const saved =
        subject.mode === 'edit'
          ? await updateCharacter(subject.character.id, trimmed)
          : await createCharacter(trimmed);
      onSaved(saved, subject.mode);
      onClose();
    } catch (caught) {
      setSubmitError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        {submitError !== null && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            <Text size="sm">{describeError(submitError)}</Text>
          </Alert>
        )}

        <Group grow align="flex-start">
          <TextInput
            label="Name"
            description="What the briefing card calls them."
            placeholder="The waiter"
            required
            data-autofocus
            value={draft.name}
            error={fieldErrors.name}
            onChange={(event) =>
              setDraft({ ...draft, name: event.currentTarget.value })
            }
          />
          <TextInput
            label="Initial"
            description="One letter, for the avatar."
            placeholder="W"
            required
            maxLength={4}
            value={draft.initial}
            error={fieldErrors.initial}
            onChange={(event) =>
              setDraft({ ...draft, initial: event.currentTarget.value })
            }
          />
        </Group>

        <Textarea
          autosize
          minRows={2}
          label="Blurb"
          description="How they are, read before meeting them. This is the whole basis for choosing one, so it has to say what the conversation will be like."
          placeholder="Patient. Slows down, repeats himself, lets things go."
          required
          value={draft.blurb}
          error={fieldErrors.blurb}
          onChange={(event) =>
            setDraft({ ...draft, blurb: event.currentTarget.value })
          }
        />

        <Textarea
          autosize
          minRows={4}
          label="Tolerance"
          description="How much they carry the conversation. Written for the model, never shown to a learner. Say what they do when somebody is drowning, and what they do when somebody is fluent."
          placeholder="Endlessly patient. He will wait, repeat himself more simply, and let a mistake go rather than correct it. He never switches to English."
          required
          value={draft.tolerance}
          error={fieldErrors.tolerance}
          onChange={(event) =>
            setDraft({ ...draft, tolerance: event.currentTarget.value })
          }
        />

        <Divider
          label="What the first run says about them afterwards"
          labelPosition="left"
        />

        <TextInput
          label="Next up"
          description="A character to meet next, by name."
          placeholder="Sofia"
          required
          value={draft.nextUp}
          error={fieldErrors.nextUp}
          onChange={(event) =>
            setDraft({ ...draft, nextUp: event.currentTarget.value })
          }
        />

        <Textarea
          autosize
          minRows={2}
          label="Why them"
          description="The reason it gives. A sentence — it is one of the three specific things the first run closes on."
          placeholder="She's quicker than he is, and she'll ask you things back rather than just taking an order."
          required
          value={draft.nextUpReason}
          error={fieldErrors.nextUpReason}
          onChange={(event) =>
            setDraft({ ...draft, nextUpReason: event.currentTarget.value })
          }
        />

        <NumberInput
          label="Order"
          description={`Lower comes first, and only the first ${OFFERED} are ever offered. Leave it blank and they sit at the end.`}
          placeholder="Unranked"
          min={0}
          max={9999}
          allowDecimal={false}
          value={draft.displayOrder ?? ''}
          onChange={(value) =>
            setDraft({
              ...draft,
              displayOrder: value === '' ? null : Number(value),
            })
          }
        />

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSaving}>
            {subject.mode === 'edit' ? 'Save changes' : 'Add character'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

interface SceneFormProps {
  subject: SceneSubject;
  languages: Language[];
  /** Every voice the synthesiser offers, filtered here to this scene. */
  voices: SpeechVoice[];
  /** True when the list could not be fetched, as opposed to being empty. */
  voicesUnavailable: boolean;
  onClose: () => void;
  onSaved: (characterId: string, scene: Scene) => void;
}

/**
 * The scene editor: one character, one language, both opening turns.
 *
 * Long, and deliberately one form rather than a wizard. Everything here is read
 * by the same learner in the same ninety seconds, and the register at the top
 * is what decides how the lines at the bottom are written — splitting it across
 * steps would hide the half that governs the other half.
 */
function SceneForm({
  subject,
  languages,
  voices,
  voicesUnavailable,
  onClose,
  onSaved,
}: SceneFormProps) {
  const [draft, setDraft] = useState<SceneDraft>(() =>
    subject.scene
      ? {
          languageCode: subject.scene.languageCode,
          setting: subject.scene.setting,
          wants: subject.scene.wants,
          register: subject.scene.register,
          silencePrompt: subject.scene.silencePrompt,
          silencePromptGloss: subject.scene.silencePromptGloss,
          closing: subject.scene.closing,
          closingGloss: subject.scene.closingGloss,
          voiceName: subject.scene.voiceName,
          turns: subject.scene.turns.map((turn) => ({
            ...turn,
            suggestions: [...turn.suggestions],
          })),
        }
      : blankScene(subject.languageCode),
  );
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Filtered on this scene's language, and that is not a nicety: the provider
  // rejects a request whose language code does not match its voice, so an
  // it-IT voice on a Spanish scene would produce a café that never plays.
  const offered = voicesFor(voices, draft.languageCode);
  const inherited = languages.find(
    (language) => language.code === draft.languageCode,
  )?.ttsVoice;

  function setTurn(index: number, patch: Partial<SceneDraft['turns'][number]>) {
    setDraft((current) => ({
      ...current,
      turns: current.turns.map((turn, i) =>
        i === index ? { ...turn, ...patch } : turn,
      ),
    }));
  }

  function setSuggestion(turnIndex: number, replyIndex: number, value: string) {
    setDraft((current) => ({
      ...current,
      turns: current.turns.map((turn, i) =>
        i === turnIndex
          ? {
              ...turn,
              suggestions: turn.suggestions.map((reply, r) =>
                r === replyIndex ? value : reply,
              ),
            }
          : turn,
      ),
    }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    setIsSaving(true);
    setSubmitError(null);
    try {
      const saved = await saveScene(subject.character.id, {
        ...draft,
        // Blank arrives as null rather than "". Clearing the box is how a
        // scene stops overriding and falls back to the language's voice.
        voiceName: draft.voiceName?.trim() ? draft.voiceName.trim() : null,
      });
      onSaved(subject.character.id, saved);
      onClose();
    } catch (caught) {
      setSubmitError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  const complete = sceneIsComplete(draft);

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        {submitError !== null && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            <Text size="sm">{describeError(submitError)}</Text>
          </Alert>
        )}

        <Select
          label="Language"
          description="One scene per character per language. Picking a language that already has one opens it for editing."
          data={languages.map((language) => ({
            value: language.code,
            label: `${language.name} — ${language.nativeName}`,
          }))}
          value={draft.languageCode}
          // Locked once a scene exists: changing it here would write a second
          // scene rather than move this one, and leave the original behind.
          disabled={subject.scene !== null}
          onChange={(value) =>
            value && setDraft({ ...draft, languageCode: value })
          }
          searchable
          required
        />

        <Autocomplete
          label="Voice"
          description={
            offered.length > 0
              ? inherited
                ? `What this character sounds like. Leave it blank and they use the language's voice.`
                : 'What this character sounds like. This language has no default, so leaving it blank means the scene has no audio.'
              : voicesUnavailable
                ? 'The voice list could not be loaded, so type a name. Leave blank to use the language default.'
                : 'No synthesiser voices for this language. Its lines will have no audio.'
          }
          placeholder={inherited ?? 'No voice — this scene has no audio'}
          data={offered.map((voice) => voice.name)}
          value={draft.voiceName ?? ''}
          onChange={(value) =>
            setDraft({ ...draft, voiceName: value === '' ? null : value })
          }
        />

        <Group grow align="flex-start">
          <TextInput
            label="Setting"
            description="Where this happens, in the learner's own language."
            placeholder="A café in Madrid, mid-morning"
            required
            value={draft.setting}
            onChange={(event) =>
              setDraft({ ...draft, setting: event.currentTarget.value })
            }
          />
          <TextInput
            label="What they want"
            description="Sits under their name for the whole scene."
            placeholder="He wants your order"
            required
            value={draft.wants}
            onChange={(event) =>
              setDraft({ ...draft, wants: event.currentTarget.value })
            }
          />
        </Group>

        <Textarea
          autosize
          minRows={2}
          label="Register"
          description="Usted or tú, vous or tu. Written for the model. Getting this wrong is the kind of mistake a learner cannot see and a native speaker cannot unsee."
          placeholder="Usted throughout. Warm, formal, a working waiter in a hurry he never shows."
          required
          dir="auto"
          value={draft.register}
          onChange={(event) =>
            setDraft({ ...draft, register: event.currentTarget.value })
          }
        />

        <Divider
          label="The opening two lines, which are never generated"
          labelPosition="left"
        />

        <Text size="xs" c="dimmed">
          These are the two lines the model does not write. They land instantly,
          they are the first thing anybody reads, and the model warms up behind
          them &mdash; so they are worth more care than the rest of the scene put
          together.
        </Text>

        {draft.turns.map((turn, index) => (
          <Card key={index} withBorder padding="md">
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Turn {index + 1}
              </Text>

              <Group grow align="flex-start">
                <TextInput
                  label="What they say"
                  placeholder="¡Buenos días! ¿Qué le pongo?"
                  required
                  dir="auto"
                  value={turn.line}
                  onChange={(event) =>
                    setTurn(index, { line: event.currentTarget.value })
                  }
                />
                <TextInput
                  label="What it means"
                  description="Revealed by “what did they say?”"
                  placeholder="Good morning! What can I get you?"
                  required
                  value={turn.gloss}
                  onChange={(event) =>
                    setTurn(index, { gloss: event.currentTarget.value })
                  }
                />
              </Group>

              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Three suggested replies
                </Text>
                <Text size="xs" c="dimmed">
                  Always on screen. Somebody who knows no words at all has to be
                  able to finish this scene by tapping alone.
                </Text>
                {turn.suggestions.map((reply, replyIndex) => (
                  <TextInput
                    key={replyIndex}
                    placeholder={
                      ['Un café, por favor', '¿Qué tienen?', 'Un momento'][
                        replyIndex
                      ]
                    }
                    required
                    dir="auto"
                    value={reply}
                    onChange={(event) =>
                      setSuggestion(index, replyIndex, event.currentTarget.value)
                    }
                  />
                ))}
              </Stack>

              <Group grow align="flex-start">
                <TextInput
                  label="Say it for me"
                  description="What that lifeline sends. Allowed to be the politer answer."
                  placeholder="Un café, por favor"
                  required
                  dir="auto"
                  value={turn.sayItForMe}
                  onChange={(event) =>
                    setTurn(index, { sayItForMe: event.currentTarget.value })
                  }
                />
                <TextInput
                  label="Hint"
                  description="In the learner's own language."
                  placeholder='He’s asking what you want. "Un café" is enough.'
                  required
                  value={turn.hint}
                  onChange={(event) =>
                    setTurn(index, { hint: event.currentTarget.value })
                  }
                />
              </Group>
            </Stack>
          </Card>
        ))}

        <Divider label="How the scene handles itself" labelPosition="left" />

        <Group grow align="flex-start">
          <TextInput
            label="Silence prompt"
            description="Said after twenty seconds of nothing, in the target language."
            placeholder="¿Sigue ahí? Tómese su tiempo."
            required
            dir="auto"
            value={draft.silencePrompt}
            onChange={(event) =>
              setDraft({ ...draft, silencePrompt: event.currentTarget.value })
            }
          />
          <TextInput
            label="What it means"
            placeholder="Still there? Take your time."
            required
            value={draft.silencePromptGloss}
            onChange={(event) =>
              setDraft({
                ...draft,
                silencePromptGloss: event.currentTarget.value,
              })
            }
          />
        </Group>

        <Group grow align="flex-start">
          <TextInput
            label="Closing line"
            description="Said when they got what they wanted."
            placeholder="Muy bien. Ahora mismo se lo llevo."
            required
            dir="auto"
            value={draft.closing}
            onChange={(event) =>
              setDraft({ ...draft, closing: event.currentTarget.value })
            }
          />
          <TextInput
            label="What it means"
            placeholder="Lovely. I'll bring it straight over."
            required
            value={draft.closingGloss}
            onChange={(event) =>
              setDraft({ ...draft, closingGloss: event.currentTarget.value })
            }
          />
        </Group>

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!complete}>
            Save scene
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

interface RemoveFormProps {
  character: Character;
  onClose: () => void;
  onRemoved: (character: Character) => void;
}

/**
 * Confirmation before a delete.
 *
 * A hard delete, and it takes every scene with it — so removing a character
 * throws away every language anybody has authored them into, not just the row.
 */
function RemoveForm({ character, onClose, onRemoved }: RemoveFormProps) {
  const [error, setError] = useState<unknown>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  async function confirm() {
    setIsRemoving(true);
    try {
      await deleteCharacter(character.id);
      onRemoved(character);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <Stack gap="md">
      {error !== null && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      )}

      <Text size="sm">
        {character.scenes.length > 0
          ? `This also deletes ${character.scenes.length} authored ${
              character.scenes.length === 1 ? 'scene' : 'scenes'
            }, including every line written for them. There is no undo.`
          : 'Nobody has authored a scene for them yet. There is no undo.'}
      </Text>

      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          color="gray"
          onClick={onClose}
          disabled={isRemoving}
        >
          Cancel
        </Button>
        <Button color="red" loading={isRemoving} onClick={() => void confirm()}>
          Remove {character.name}
        </Button>
      </Group>
    </Stack>
  );
}

export function CharactersPage() {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [voices, setVoices] = useState<SpeechVoice[]>([]);
  const [voicesUnavailable, setVoicesUnavailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);

  const [subject, setSubject] = useState<Subject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [sceneSubject, setSceneSubject] = useState<SceneSubject | null>(null);
  const [isSceneOpen, setSceneOpen] = useState(false);
  const [removing, setRemoving] = useState<Character | null>(null);
  const [isRemoveOpen, setRemoveOpen] = useState(false);

  function openForm(next: Subject) {
    setSubject(next);
    setFormOpen(true);
  }

  function openScene(next: SceneSubject) {
    setSceneSubject(next);
    setSceneOpen(true);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // Both, because the scene editor cannot offer a language picker
        // without the catalogue and the two are independent reads.
        const [cast, catalogue] = await Promise.all([
          listCharacters(),
          listLanguages(),
        ]);
        if (cancelled) return;
        setCharacters(cast);
        setLanguages(catalogue);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }

      // Fetched separately and allowed to fail, the way the model list is on
      // the language model screen. A deployment with no synthesiser still has
      // a cast to write, so the voice field falls back to free text rather
      // than the page falling over.
      try {
        const list = await listVoices();
        if (!cancelled) setVoices(list);
      } catch (caught) {
        if (cancelled) return;
        setVoices([]);
        // "No synthesiser configured" is not the same as "the call failed",
        // and only the second is worth telling somebody to type around.
        setVoicesUnavailable(
          !(
            caught instanceof ApiError &&
            caught.is(ApiErrorCode.SPEECH_NOT_CONFIGURED)
          ),
        );
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The order the API sorts by, held locally so an edit lands in place.
   *
   * It has to match `CAST_ORDER` in the API exactly: display order first with
   * unranked at the back, then name.
   */
  function sorted(list: Character[]): Character[] {
    return [...list].sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) {
        if (a.displayOrder === null) return 1;
        if (b.displayOrder === null) return -1;
        return a.displayOrder - b.displayOrder;
      }
      return a.name.localeCompare(b.name);
    });
  }

  function onSaved(saved: Character, mode: Subject['mode']) {
    setCharacters((current) =>
      sorted(
        mode === 'edit'
          ? current.map((item) => (item.id === saved.id ? saved : item))
          : [...current, saved],
      ),
    );
    notifications.show({
      color: 'brand',
      message:
        mode === 'edit' ? `${saved.name} updated.` : `${saved.name} added.`,
    });
  }

  function onSceneSaved(characterId: string, scene: Scene) {
    setCharacters((current) =>
      current.map((character) =>
        character.id === characterId
          ? {
              ...character,
              scenes: sortScenes([
                ...character.scenes.filter(
                  (existing) => existing.languageCode !== scene.languageCode,
                ),
                scene,
              ]),
            }
          : character,
      ),
    );
    notifications.show({
      color: 'brand',
      message: `${scene.languageCode.toUpperCase()} scene saved.`,
    });
  }

  async function removeScene(character: Character, scene: Scene) {
    try {
      await deleteScene(character.id, scene.languageCode);
      setCharacters((current) =>
        current.map((item) =>
          item.id === character.id
            ? {
                ...item,
                scenes: item.scenes.filter((s) => s.id !== scene.id),
              }
            : item,
        ),
      );
      notifications.show({
        color: 'gray',
        message: `${scene.languageCode.toUpperCase()} scene removed.`,
      });
    } catch (caught) {
      notifications.show({ color: 'red', message: describeError(caught) });
    }
  }

  function onRemoved(removed: Character) {
    setCharacters((current) => current.filter((item) => item.id !== removed.id));
    notifications.show({ color: 'gray', message: `${removed.name} removed.` });
  }

  const header = (
    <PageHeader
      title="Cast"
      description="Who a learner meets in the first run, and what they open with."
      actions={
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openForm({ mode: 'create' })}
          disabled={isLoading || loadError !== null}
        >
          Add character
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Alert
          color="red"
          title="Cannot load the cast"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  /**
   * Which languages have a café at all.
   *
   * The most important thing on this page. A learner whose language is missing
   * here silently gets the Spanish scene bundled in the app instead — it does
   * not fail, it does not warn them, and this table is the only place anybody
   * can see it happening.
   */
  const ready = new Set(
    characters.flatMap((character) =>
      character.scenes.map((scene) => scene.languageCode),
    ),
  );
  const unauthored = languages.filter(
    (language) => !ready.has(language.code.toLowerCase()),
  );

  return (
    <Stack gap="lg" className={classes.page}>
      {header}

      {unauthored.length > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">
            No café is authored in{' '}
            {unauthored.map((language) => language.name).join(', ')}. Anybody
            choosing one of those in the first run gets the Spanish scene
            bundled in the app instead, with no error and nothing to tell them.
          </Text>
        </Alert>
      )}

      {characters.length === 0 ? (
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconMasksTheater size={28} stroke={1.4} />
            <Text fw={600}>No cast yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={420}>
              Nobody has been written. Until somebody is, every first run falls
              back to the two characters bundled in the app.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card p={0}>
          <Table.ScrollContainer minWidth={720}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>Order</Table.Th>
                  <Table.Th>Character</Table.Th>
                  <Table.Th>Scenes</Table.Th>
                  <Table.Th w={90} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {characters.map((character, index) => (
                  <Table.Tr key={character.id}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" c="dimmed">
                          {character.displayOrder ?? '—'}
                        </Text>
                        {/* Everything past the first two is authored and
                            stored but never offered. Saying so beats somebody
                            wondering why their new character never appears. */}
                        {index >= OFFERED && (
                          <Badge variant="light" color="gray" size="xs">
                            Not offered
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" fw={600}>
                          {character.name}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {character.blurb}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6}>
                        {character.scenes.map((scene) => (
                          <Badge
                            key={scene.id}
                            variant="light"
                            className={classes.code}
                            rightSection={
                              <ActionIcon
                                size="xs"
                                variant="transparent"
                                color="gray"
                                aria-label={`Remove the ${scene.languageCode.toUpperCase()} scene for ${character.name}`}
                                onClick={(event) => {
                                  // The badge opens the editor. Without this,
                                  // deleting a scene also opens the scene it
                                  // just deleted.
                                  event.stopPropagation();
                                  void removeScene(character, scene);
                                }}
                              >
                                <IconTrash size={11} stroke={1.7} />
                              </ActionIcon>
                            }
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              openScene({
                                character,
                                scene,
                                languageCode: scene.languageCode,
                              })
                            }
                          >
                            {scene.languageCode.toUpperCase()}
                          </Badge>
                        ))}
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<IconPlus size={12} />}
                          onClick={() =>
                            openScene({
                              character,
                              scene: null,
                              languageCode: languages[0]?.code ?? 'es',
                            })
                          }
                        >
                          Language
                        </Button>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Edit ${character.name}`}
                          onClick={() => openForm({ mode: 'edit', character })}
                        >
                          <IconPencil size={16} stroke={1.7} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${character.name}`}
                          onClick={() => {
                            setRemoving(character);
                            setRemoveOpen(true);
                          }}
                        >
                          <IconTrash size={16} stroke={1.7} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Text size="xs" c="dimmed">
        The first run offers the first {OFFERED} of these, as a choice between
        two people rather than a roster. Each needs a scene in every language it
        should be playable in &mdash; the two opening lines are authored here and
        are the only ones the model does not write.
      </Text>

      <Modal
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        centered
        size="lg"
        title={
          subject?.mode === 'edit'
            ? `Edit ${subject.character.name}`
            : 'Add a character'
        }
      >
        {subject && (
          <CharacterForm
            key={subject.mode === 'edit' ? subject.character.id : 'create'}
            subject={subject}
            onClose={() => setFormOpen(false)}
            onSaved={onSaved}
          />
        )}
      </Modal>

      <Modal
        opened={isSceneOpen}
        onClose={() => setSceneOpen(false)}
        centered
        size="xl"
        title={
          sceneSubject
            ? `${sceneSubject.character.name} — ${
                sceneSubject.scene
                  ? `${sceneSubject.scene.languageCode.toUpperCase()} scene`
                  : 'a new scene'
              }`
            : 'Scene'
        }
      >
        {sceneSubject && (
          <SceneForm
            key={`${sceneSubject.character.id}:${sceneSubject.scene?.id ?? 'new'}`}
            subject={sceneSubject}
            languages={languages}
            voices={voices}
            voicesUnavailable={voicesUnavailable}
            onClose={() => setSceneOpen(false)}
            onSaved={onSceneSaved}
          />
        )}
      </Modal>

      <Modal
        opened={isRemoveOpen}
        onClose={() => setRemoveOpen(false)}
        centered
        title={`Remove ${removing?.name ?? 'character'}?`}
      >
        {removing && (
          <RemoveForm
            key={removing.id}
            character={removing}
            onClose={() => setRemoveOpen(false)}
            onRemoved={onRemoved}
          />
        )}
      </Modal>
    </Stack>
  );
}

/** The order the API returns scenes in, so a save lands in place. */
function sortScenes(scenes: Scene[]): Scene[] {
  return [...scenes].sort((a, b) => a.languageCode.localeCompare(b.languageCode));
}
